// Tax export card — Oct 3 2026 (Randy, F7 decisions 1A + 2C).
//
// Before: a card that said the current year, with no year control at all (so
// earlier years were undownloadable even though the endpoint always accepted
// ?year=), and a download whose contents you could only learn by opening the
// file. It also never contained a single renewal.
//
// Now: pick the year, see what is in it BEFORE downloading, and see the old
// method's figure beside the new one — because renewal-inclusive reporting
// raises totals for years that may already have been filed, and that
// difference should be a reported number rather than a surprise.
import { useEffect, useState } from "react";
import { FileArrowDown, CaretDown, CaretRight, Info } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const fmt$ = (cents) => {
  const n = (cents || 0) / 100;
  return `${n < 0 ? "−" : ""}$${Math.abs(n).toLocaleString(undefined, {
    minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const LINE_LABEL = {
  tip_received: "Tips",
  subscription_started: "Subscriptions · first period",
  subscription_renewal: "Subscriptions · renewals",
  referral_payout: "Referral commission",
};

export default function TaxExportCard() {
  const [year, setYear] = useState(new Date().getFullYear());
  const [sum, setSum] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [openNotes, setOpenNotes] = useState(false);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    api.get(`/me/operator-os/tax-summary?year=${year}`)
      .then((r) => { if (!cancel) setSum(r.data); })
      .catch(() => { if (!cancel) setSum(null); })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [year]);

  const download = async () => {
    setDownloading(true);
    try {
      const r = await api.get(`/me/operator-os/tax-csv?year=${year}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `mysilo-tax-export-${year}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Tax CSV for ${year} downloaded.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "CSV export failed.");
    } finally { setDownloading(false); }
  };

  const years = sum?.available_years?.length ? sum.available_years : [year];
  const diff = sum?.difference?.gross_cents || 0;
  const renewals = sum?.by_kind?.subscription_renewal;

  return (
    <div className="bg-white border hairline rounded-xl p-5 flex flex-col" data-testid="operator-os-tax-card">
      <div className="flex items-start justify-between mb-2">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#6B6A68]">Tax export</div>
          <select
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            data-testid="tax-year-select"
            className="mt-1 font-serif-display text-2xl text-[#1C1B1A] bg-transparent border-0 border-b border-dashed border-[#D8D6D2] focus:outline-none focus:border-[#A89360] cursor-pointer pr-1"
          >
            {years.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
        <div className="w-9 h-9 rounded-md bg-[#F0F1EE] flex items-center justify-center text-[#4A5D4E] shrink-0">
          <FileArrowDown size={16} weight="duotone" />
        </div>
      </div>

      {loading ? (
        <p className="text-[11px] text-[#6B6A68] italic flex-1">Adding it up…</p>
      ) : !sum ? (
        <p className="text-[11px] text-[#6B6A68] flex-1">
          Tips, subscription revenue and referral payouts — ready for your accountant.
        </p>
      ) : (
        <div className="flex-1">
          <div className="flex items-baseline gap-2">
            <span className="font-serif-display text-2xl text-[#1C1B1A] tabular-nums"
                  data-testid="tax-summary-net">
              {fmt$(sum.totals.net_cents)}
            </span>
            <span className="text-[10px] uppercase tracking-[0.16em] text-[#6B6A68]">
              yours · {sum.totals.count} line{sum.totals.count === 1 ? "" : "s"}
            </span>
          </div>
          <div className="mt-2 space-y-0.5" data-testid="tax-summary-breakdown">
            {Object.entries(LINE_LABEL).map(([k, label]) => {
              const a = sum.by_kind?.[k];
              if (!a || !a.count) return null;
              return (
                <div key={k} className="flex items-baseline gap-2 text-[11px]"
                     data-testid={`tax-line-${k}`}>
                  <span className="text-[#3A3936] truncate">{label}</span>
                  <span className="text-[#A9A7A2] text-[10px]">×{a.count}</span>
                  <span className="ml-auto tabular-nums text-[#1C1B1A]">{fmt$(a.net_cents)}</span>
                </div>
              );
            })}
            {!sum.totals.count && (
              <p className="text-[11px] text-[#6B6A68] italic">
                Nothing collected in {year}.
              </p>
            )}
          </div>

          {renewals?.count > 0 && (
            <div className="mt-2.5 text-[11px] text-[#2F6B3A] bg-[#F1F7F1] border border-[#C8DBCB] rounded p-2"
                 data-testid="tax-renewals-note">
              Includes {renewals.count} renewal charge{renewals.count === 1 ? "" : "s"} worth{" "}
              {fmt$(renewals.net_cents)} to you. Renewals were missing from every earlier export.
            </div>
          )}
          {diff !== 0 && (
            <div className="mt-2 text-[11px] text-[#8A5A00] bg-[#FDF8EE] border border-[#E0C79A] rounded p-2"
                 data-testid="tax-difference-note">
              Previous method reported {fmt$(sum.old_method.gross_cents)} of subscription income
              for {year}; this reports {fmt$(diff + sum.old_method.gross_cents)} —
              a difference of <strong>{fmt$(diff)}</strong>.
              {sum.old_method.records_without_payment > 0 && (
                <> {sum.old_method.records_without_payment} subscription
                  {sum.old_method.records_without_payment === 1 ? "" : "s"} that never collected
                  money {sum.old_method.records_without_payment === 1 ? "is" : "are"} no longer
                  counted as income.</>
              )}
            </div>
          )}

          <button
            type="button" onClick={() => setOpenNotes((v) => !v)}
            data-testid="tax-notes-toggle"
            className="mt-2.5 inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.16em] text-[#6B6A68] hover:text-[#1C1B1A]"
          >
            {openNotes ? <CaretDown size={10} weight="bold" /> : <CaretRight size={10} weight="bold" />}
            Notes for your accountant
          </button>
          {openNotes && (
            <ul className="mt-1.5 space-y-1.5" data-testid="tax-notes">
              {(sum.notes || []).map((n, i) => (
                <li key={i} className="text-[10.5px] text-[#6B6A68] leading-relaxed flex gap-1.5">
                  <Info size={11} weight="duotone" className="shrink-0 mt-0.5 text-[#A89360]" />
                  <span>{n}</span>
                </li>
              ))}
              {(sum.warnings || []).map((w, i) => (
                <li key={`w${i}`} className="text-[10.5px] text-[#8A5A00] leading-relaxed">
                  {w}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <button
        onClick={download}
        disabled={downloading}
        data-testid="operator-os-tax-download"
        className="mt-3 text-xs px-3 py-2 rounded-md bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#3A4A3E] flex items-center justify-center gap-1.5 disabled:opacity-50">
        <FileArrowDown size={12} weight="bold" />
        {downloading ? "Building…" : `Download ${year} CSV`}
      </button>
    </div>
  );
}
